/* <predictive-search>: the sketch's search. In the header (desktop) the field opens a panel under the bar; in the
   phone's search view the results sit under the field. Before a word: the popular searches and four products. While
   typing: Shopify's suggestions (sections/predictive-search.liquid), the newest answer only, read aloud by the status
   line. The panel follows the bar in the page (sections/header.liquid), so Tab goes field, heart, bag, results. The
   form still posts to the search page. */
const root = window.Shopify?.routes?.root || '/';
const narrow = matchMedia('(max-width: 1100px)');

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.input = this.querySelector('input[type=search]');
    this.flyout = document.getElementById(this.input.getAttribute('aria-controls'));
    this.results = this.flyout.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.idle = document.querySelector('template[data-search-idle]');
    this.clear = this.querySelector('[data-search-clear]');
    this.header = this.closest('.site-header');
    // «Søk» in a narrow field (1100 px and under, and the phone's), as the sketch.
    const long = (this.longPlaceholder ??= this.input.placeholder);
    const label = () => (this.input.placeholder = narrow.matches ? this.input.dataset.short : long);
    label();
    narrow.addEventListener('change', label, { signal });
    this.input.addEventListener('input', () => this.search(), { signal });
    // «Tøm søket» empties the field and brings back what shows before a word.
    this.clear.addEventListener(
      'click',
      () => {
        this.input.value = '';
        this.input.focus();
        this.search();
      },
      { signal },
    );
    // Focus opens it, and so does a press in the field after Escape closed it (the focus stayed).
    for (const type of ['focus', 'click']) this.input.addEventListener(type, () => this.open(), { signal });
    // A popular search fills the field.
    this.flyout.addEventListener(
      'click',
      (event) => {
        const term = event.target.closest('[data-search-term]');
        if (!term) return;
        event.preventDefault();
        this.input.value = term.dataset.searchTerm;
        this.input.focus();
        this.search();
      },
      { signal },
    );
    for (const part of [this, this.flyout]) {
      part.addEventListener('keydown', (event) => event.key === 'Escape' && this.escape(event), { signal });
      // Focus that leaves the search closes it: the field, the heart and bag between it and the panel (the Tab
      // order), the panel, and the panel shell a card may open count as inside.
      part.addEventListener(
        'focusout',
        (event) => {
          const next = event.relatedTarget;
          if (next && !next.closest('.header-actions, [data-search-flyout], dialog')) this.leave();
        },
        { signal },
      );
    }
    this.onOutside = (event) => {
      if (!this.contains(event.target) && !this.flyout.contains(event.target)) this.close();
    };
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.leave();
    this.events?.abort();
  }

  /** Before a word: the popular searches and four products (built on the first opening, not on load). */
  showIdle() {
    this.results.replaceChildren(this.idle.content.cloneNode(true));
  }

  open() {
    if (!this.flyout.hidden) return;
    if (!this.results.childElementCount) this.showIdle();
    this.flyout.hidden = false;
    document.documentElement.toggleAttribute('data-searching', true);
    document.addEventListener('pointerdown', this.onOutside, {
      signal: this.events.signal,
    });
  }

  close() {
    if (this.flyout.hidden) return;
    this.flyout.hidden = true;
    document.documentElement.toggleAttribute('data-searching', false);
    document.removeEventListener('pointerdown', this.onOutside);
  }

  /** Closes, and on the phone gives the header back. */
  leave() {
    this.close();
    this.header.classList.remove('is-searching');
  }

  /** Escape keeps the words, also when the search is already closed (the browser would empty a search field). On the
      desktop focus goes to the field before the search closes, so the field's focus opens nothing; on the phone it
      goes back to the search icon, which shows once the header is given back. */
  escape(event) {
    event.preventDefault();
    if (this.flyout.hidden) return;
    const phone = this.header.classList.contains('is-searching');
    if (!phone) this.input.focus();
    this.leave();
    if (phone) this.header.querySelector('[data-search-open]').focus();
  }

  showFailure(terms) {
    const group = document.createElement('section');
    group.className = 'search-group';
    const message = document.createElement('h2');
    message.className = 'eyebrow';
    message.dataset.searchSay = '';
    message.textContent = this.dataset.failed;
    const link = document.createElement('a');
    link.className = 'text-link';
    link.href = `${root}search?q=${encodeURIComponent(terms)}&type=product&options[unavailable_products]=show&options[prefix]=last`;
    link.textContent = this.dataset.allResults.replace('[terms]', terms);
    group.append(message, link);
    this.results.replaceChildren(group);
    this.status.textContent = message.textContent;
  }

  search() {
    const terms = this.input.value.trim();
    this.open();
    this.clear.hidden = !this.input.value;
    clearTimeout(this.timer);
    this.request?.abort();
    if (!terms) {
      this.status.textContent = '';
      this.results.removeAttribute('aria-busy');
      return this.showIdle();
    }
    this.results.setAttribute('aria-busy', 'true');
    this.timer = setTimeout(async () => {
      const request = (this.request = new AbortController());
      const url = `${root}search/suggest?q=${encodeURIComponent(terms)}&resources[type]=product,collection&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=show&section_id=predictive-search`;
      try {
        const html = await fetch(url, { signal: request.signal }).then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        });
        if (!this.isConnected || request !== this.request || request.signal.aborted) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-results]');
        if (!fresh) throw new Error('Missing search results section');
        this.results.replaceChildren(...fresh.childNodes);
        this.status.textContent = [...this.results.querySelectorAll('[data-search-say]')]
          .map((group) => group.textContent.trim())
          .filter(Boolean)
          .join('. ');
      } catch {
        if (this.isConnected && request === this.request && !request.signal.aborted) this.showFailure(terms);
      } finally {
        if (request === this.request && !request.signal.aborted) this.results.removeAttribute('aria-busy');
      }
    }, 120);
  }
}

customElements.define('predictive-search', PredictiveSearch);

/* Under 1024 px the search icon turns the header into the field, as the sketch: the field and «Avbryt», the results
   under it. «Avbryt» empties it, gives the header back and returns to the search icon. */
document.addEventListener('click', (event) => {
  const header = document.querySelector('.site-header');
  const search = header?.querySelector('predictive-search');
  if (!search) return;
  if (event.target.closest('[data-search-open]')) {
    event.preventDefault();
    // The results reach the bottom of the screen from wherever the header ends (the notice may be above it).
    header.style.setProperty('--search-top', `${header.getBoundingClientRect().bottom}px`);
    header.classList.add('is-searching');
    search.input.focus();
  } else if (event.target.closest('[data-search-close]')) {
    search.input.value = '';
    search.search();
    search.leave();
    header.querySelector('[data-search-open]').focus();
  }
});
